import { Theme, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import Layout from '@common/layout/Layout';
import type { AppProps } from 'next/app';
import { PropsWithChildren, ReactElement, ReactNode, useEffect, useState } from 'react';
import type {} from '@mui/lab/themeAugmentation';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { DataProvider } from '@common/contexts/DataContext';
import palette from '@common/theme/palette';
import typography from '@common/theme/typography';
import shadows from '@common/theme/shadows';
import ProgressBar from '@common/components/lib/feedbacks/ProgressBar';
import LoadingScreen from '@common/components/lib/feedbacks/LoadingScreen';
import customShadows from '@common/theme/customShadows';
import SnackbarProvider from '@common/contexts/SnackbarProvider';
import GlobalStyles from '@common/theme/GlobalStyles';
import ComponentsOverrides from '@common/theme/ComponentsOverrides';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import { RoutingHistoryProvider } from '@common/contexts/RoutingHistoryContext';
import { DialogProvider } from '@common/contexts/DialogContext';
import { PwaProvider } from '@common/contexts/PwaContext';
import { AuthFlowProvider } from '@modules/auth/contexts/AuthFlowContext';
import { frFR } from '@mui/material/locale';
import { useRouter } from 'next/router';
import { NextPage } from 'next';
import Head from 'next/head';
import ApiRoutes from '@common/defs/api-routes';
import PostHogProvider from '@common/providers/PostHogProvider';
import posthog from 'posthog-js';
import Script from 'next/script';
import { Any } from '@common/defs/types';
import { useTracking } from '@common/hooks/useTracking';
import BannedUserOverlay from '@modules/auth/components/common/BannedUserOverlay';
import '../styles/global.css';

declare global {
  interface Window {
    fbq?: Any;
    gtag?: (...args: Any[]) => void;
    dataLayer?: Any[];
  }
}

export const GOOGLE_ADS_ID = 'AW-17526606854';
export type NextPageWithLayout<P, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout<Record<string, unknown>>;
};

export const AppWrapper = ({ children }: PropsWithChildren) => {
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [theme, setTheme] = useState<Theme | null>(null);
  const router = useRouter();
  const { initialized: authInitialized, user } = useAuth();
  const { meta: metaB2B } = useTracking();

  const { updateUser } = metaB2B;

  useEffect(() => {
    setRootElement(() => document.querySelector('#__next'));
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && user) {
      posthog.identify(String(user.id) || user.email, {
        ...user,
        availableSlots: undefined,
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user || typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || (router.query.ref as string);
    if (!ref) {
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      return;
    }
    fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/set-referrer`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ref: Number(ref) }),
    }).catch(() => {});
  }, [user, router.query.ref]);

  useEffect(() => {
    if (!authInitialized) {
      metaB2B.init();
      return;
    }

    if (!user) {
      return;
    }

    const isCompleteRegistrationRoute = router.pathname === ApiRoutes.Auth.CompleteRegistration;
    const isSocialAuthRoute = router.pathname === ApiRoutes.Auth.Social;
    const isLoginRoute = router.pathname === ApiRoutes.Auth.Login;
    const isRegisterRoute = router.pathname === ApiRoutes.Auth.Register;
    const isVerifyEmailRoute = router.pathname === ApiRoutes.Auth.VerifyEmail;
    const isAuthPage = router.pathname === '/auth';

    if (user.registrationStep && user.registrationStep !== 'completed') {
      if (
        !isCompleteRegistrationRoute &&
        !isSocialAuthRoute &&
        !isLoginRoute &&
        !isRegisterRoute &&
        !isVerifyEmailRoute &&
        !isAuthPage
      ) {
        if (typeof window !== 'undefined') {
          posthog.capture('incomplete_registration_redirect', {
            step: user.registrationStep,
            from: router.pathname,
          });
        }
        router.replace(ApiRoutes.Auth.CompleteRegistration);
      }
    }

    updateUser(user);
  }, [user, authInitialized, router.pathname]);

  useEffect(() => {
    if (rootElement) {
      setTheme(() =>
        createTheme(
          {
            palette,
            typography,
            shape: { borderRadius: 12 },
            shadows,
            customShadows,
            components: ComponentsOverrides,
            breakpoints: {
              values: {
                xs: 0,
                sm: 600,
                md: 960,
                lg: 1280,
                xl: 1920,
              },
            },
          },
          frFR
        )
      );
    }
  }, [rootElement]);

  if (!theme || !authInitialized) {
    return <LoadingScreen />;
  }
  return (
    <>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          <PostHogProvider>
            <DataProvider>
              <RoutingHistoryProvider>
                <SnackbarProvider>
                  <DialogProvider>
                    <PwaProvider>
                      <AuthFlowProvider>
                        <ProgressBar />
                        {user?.isBanned ? (
                          <BannedUserOverlay banReason={user.banReason} />
                        ) : (
                          children
                        )}
                      </AuthFlowProvider>
                    </PwaProvider>
                  </DialogProvider>
                </SnackbarProvider>
              </RoutingHistoryProvider>
            </DataProvider>
          </PostHogProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      posthog.capture('$pageview');
    }
  }, []);

  const getLayout = (page: ReactElement) => {
    if (Component.getLayout) {
      return <AppWrapper>{Component.getLayout(page)}</AppWrapper>;
    }
    return (
      <AppWrapper>
        <Layout>{page}</Layout>
      </AppWrapper>
    );
  };
  return (
    <>
      <Head>
        {/* Favicon */}
        <link key="favicon" rel="icon" href="/favicon.ico" />

        {/* Meta */}
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
        <meta key="author" name="author" content={process.env.NEXT_PUBLIC_APP_TITLE} />
        <meta
          key="description"
          name="description"
          content="AlloClients — l'application qui aide les thérapeutes à remplir leur agenda automatiquement, gérer leurs rendez-vous et leur facturation. Synchronisation Google Agenda, paiement en ligne, communication client."
        />
        <meta
          key="keywords"
          name="keywords"
          content="alloclients, thérapeute, réflexologie, prise de rendez-vous, agenda en ligne, automatisation cabinet, gestion clients, facturation"
        />

        {/* Open Graph Meta Tags */}
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content="#2563eb" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="AlloClients" />
        <link rel="apple-touch-icon" href="/icons/icon-192x192.png" />
        <link rel="apple-touch-icon" sizes="72x72" href="/icons/icon-72x72.png" />
        <link rel="apple-touch-icon" sizes="96x96" href="/icons/icon-96x96.png" />
        <link rel="apple-touch-icon" sizes="128x128" href="/icons/icon-128x128.png" />
        <link rel="apple-touch-icon" sizes="144x144" href="/icons/icon-144x144.png" />
        <link rel="apple-touch-icon" sizes="152x152" href="/icons/icon-152x152.png" />
        <link rel="apple-touch-icon" sizes="192x192" href="/icons/icon-192x192.png" />
        <link rel="apple-touch-icon" sizes="384x384" href="/icons/icon-384x384.png" />
        <link rel="apple-touch-icon" sizes="512x512" href="/icons/icon-512x512.png" />

        <meta key="og:title" property="og:title" content={process.env.NEXT_PUBLIC_APP_TITLE} />
        <meta
          key="og:description"
          property="og:description"
          content="L'application qui aide les thérapeutes à remplir leur agenda automatiquement."
        />
        <meta
          key="og:image"
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_APP_URL}/logos/logo-512x512.png`}
        />
        <meta key="og:url" property="og:url" content={process.env.NEXT_PUBLIC_APP_URL} />
        <meta
          key="og:site_name"
          property="og:site_name"
          content={process.env.NEXT_PUBLIC_APP_TITLE}
        />
        <meta key="og:type" property="og:type" content="website" />

        {/* Twitter Meta Tags */}
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:url" name="twitter:url" content={process.env.NEXT_PUBLIC_APP_URL} />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={process.env.NEXT_PUBLIC_APP_TITLE}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content="L'application qui aide les thérapeutes à remplir leur agenda automatiquement."
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={`${process.env.NEXT_PUBLIC_APP_URL}/logos/logo-512x512.png`}
        />

        {/* Google / Search Engine Meta Tags */}
        <meta key="itemProp:name" itemProp="name" content={process.env.NEXT_PUBLIC_APP_TITLE} />
        <meta
          key="itemProp:description"
          itemProp="description"
          content="L'application qui aide les thérapeutes à remplir leur agenda automatiquement."
        />
        <meta
          key="itemProp:image"
          itemProp="image"
          content={`${process.env.NEXT_PUBLIC_APP_URL}/logos/logo-512x512.png`}
        />

        {/* Structured Data - Schema.org */}
        <script
          key="json-ld"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: process.env.NEXT_PUBLIC_APP_TITLE,
              url: process.env.NEXT_PUBLIC_APP_URL,
              logo: `${process.env.NEXT_PUBLIC_APP_URL}/logos/logo-512x512.png`,
              description:
                "L'application qui aide les thérapeutes à remplir leur agenda automatiquement.",
            }),
          }}
        />
      </Head>
      <Script
        id="fb-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
            (window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '2358835074595889'); // Dataset B2B
          `,
        }}
      />
      <Script
        id="gtag-src"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GOOGLE_ADS_ID}');
          `,
        }}
      />
      {/* {getLayout === null ? <Component {...pageProps} /> : getLayout(<Component {...pageProps} />)} */}
      {getLayout === null ? <Component {...pageProps} /> : getLayout(<Component {...pageProps} />)}
    </>
  );
};

export default App;
